// ─────────────────────────────────────────────────────────────
// src/pages/DashboardReimbursementIncomes.jsx
// Dashboard to track *income* records tied to Federal
// reimbursement programs (school-year view, charts + table)
// ─────────────────────────────────────────────────────────────
import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    useTheme,
} from "@mui/material";
import { styled, alpha, darken, lighten } from "@mui/material/styles";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
} from "recharts";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";

import { tokens } from "../../theme";
import StatBox from "../../component-files/StatBox";
import Header from "../../component-files/Header";

// ─────────────────────────────────────────────────────────────
// Useful constants
// ─────────────────────────────────────────────────────────────
const API_INCOMES = "https://figazuresql.swancreate.com/api/swa/FINANCE_Incomes";
const API_PROGRAMS = "https://figazuresql.swancreate.com/api/swa/FINANCE_ReimbursementPrograms";

const BRIGHT_PRIMARY_COLORS = [
    "#FF0000", "#FFA500", "#FFFF00",
    "#00FF00", "#00FFFF", "#0000FF", "#FF00FF"
];

const formatCurrency = (v) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(v);

// ─────────────────────────────────────────────────────────────
// Re-usable DataGrid wrapper that respects MUI light/dark mode
// AND guarantees zebra striping with :nth-of-type() selectors.
// ─────────────────────────────────────────────────────────────
const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
    border: "none",
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,

    /* column header row */
    "& .MuiDataGrid-columnHeaders": {
        backgroundColor:
            theme.palette.mode === "dark"
                ? darken(theme.palette.background.paper, 0.10)
                : lighten(theme.palette.background.paper, 0.06),
        borderBottom: `2px solid ${theme.palette.secondary.main}`,
        color: theme.palette.text.primary,
    },

    /* footer (pagination) */
    "& .MuiDataGrid-footerContainer": {
        backgroundColor:
            theme.palette.mode === "dark"
                ? darken(theme.palette.background.paper, 0.10)
                : lighten(theme.palette.background.paper, 0.06),
        borderTop: `1px solid ${theme.palette.secondary.main}`,
        color: theme.palette.text.primary,
    },

    /* cell divider */
    "& .MuiDataGrid-cell": {
        borderBottom: `1px solid ${theme.palette.secondary.main}`,
    },

    /* reliable zebra striping — works with or without getRowClassName */
    "& .MuiDataGrid-row:nth-of-type(even)": {
        backgroundColor:
            theme.palette.mode === "dark"
                ? alpha(theme.palette.primary.light, 0.15)
                : alpha(theme.palette.primary.main, 0.04),
    },
    "& .MuiDataGrid-row:nth-of-type(odd)": {
        backgroundColor:
            theme.palette.mode === "dark"
                ? alpha(theme.palette.primary.main, 0.10)
                : alpha(theme.palette.primary.light, 0.08),
    },
}));

// ─────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────
const DashboardReimbursementIncomes = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    // raw API data
    const [incomes, setIncomes] = useState([]);
    const [programs, setPrograms] = useState([]);

    // filter state
    const [selectedMonth, setSelectedMonth] = useState("All");
    const [selectedProgram, setSelectedProgram] = useState("All");

    // ── Fetch data once on mount
    useEffect(() => {
        const fetchAll = async () => {
            try {
                const [incRes, progRes] = await Promise.all([
                    axios.get(API_INCOMES),
                    axios.get(API_PROGRAMS),
                ]);

                // Normalise income rows
                const incData = incRes.data.map((r) => ({
                    ...r,
                    IncomeDate: r.IncomeDate ? new Date(r.IncomeDate) : null,
                    Amount: parseFloat(r.Amount) || 0,
                    ReimbursementTrackingId: r.ReimbursementTrackingId?.toString() ?? "",
                }));

                const progData = progRes.data.map((p) => ({
                    ...p,
                    programid: p.programid.toString(),
                }));

                setIncomes(incData);
                setPrograms(progData);
            } catch (err) {
                console.error("Fetch error:", err);
            }
        };
        fetchAll();
    }, []);

    // ── Filter helpers
    const filtered = incomes.filter((inc) => {
        if (!inc.ReimbursementTrackingId) return false; // only rows tied to a program

        const monthOK =
            selectedMonth === "All" ||
            inc.IncomeDate?.getMonth() + 1 === Number(selectedMonth);

        const progOK =
            selectedProgram === "All" ||
            inc.ReimbursementTrackingId === selectedProgram;

        return monthOK && progOK;
    });

    // ── Totals by program (for stat boxes & pie)
    const totalByProgram = filtered.reduce((acc, row) => {
        const key = row.ReimbursementTrackingProgram;
        acc[key] = (acc[key] || 0) + row.Amount;
        return acc;
    }, {});
    const sortedProgramEntries = Object.entries(totalByProgram)
        .sort(([, a], [, b]) => b - a);

    // pie chart data
    const pieData = sortedProgramEntries.map(([name, value]) => ({ name, value }));

    // ── Build Jul→Jun school-year monthly series
    const monthlyData = (() => {
        const now = new Date();
        const startYear = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
        const rows = Array.from({ length: 12 }, (_, i) => {
            const d = new Date(startYear, 6 + i, 1);
            return { month: d.toLocaleString("default", { month: "short" }) };
        });

        filtered.forEach((inc) => {
            const d = inc.IncomeDate;
            if (!d) return;
            const idx = (d.getMonth() - 6 + 12) % 12; // Jul=0 … Jun=11
            const prog = inc.ReimbursementTrackingProgram;
            rows[idx][prog] = (rows[idx][prog] || 0) + inc.Amount;
        });

        // fill missing series with 0 so Recharts draws lines correctly
        rows.forEach((row) =>
            sortedProgramEntries.forEach(([prog]) => {
                if (row[prog] == null) row[prog] = 0;
            })
        );

        return rows;
    })();

    // unique months present (for dropdown)
    const monthOptions = Array.from(
        new Set(incomes.map((i) => i.IncomeDate?.getMonth() + 1).filter(Boolean))
    ).sort((a, b) => a - b);

    // ── DataGrid column definitions
    const columns = [
        { field: "IncomeID", headerName: "Income ID", width: 180, valueGetter: p => p.row.IncomeID || p.row.apiid },
        { field: "ProcessID", headerName: "Process ID", width: 130 },
        {
            field: "IncomeDate", headerName: "Date", width: 130,
            valueGetter: p => p.row.IncomeDate?.toLocaleDateString() || ""
        },
        {
            field: "Amount", headerName: "Amount", width: 130,
            valueGetter: p => formatCurrency(p.row.Amount)
        },
        { field: "IncomeCategory", headerName: "Category", width: 140 },
        { field: "IncomeSubcategory", headerName: "Subcategory", width: 160 },
        { field: "Description", headerName: "Description", width: 240 },
        { field: "DistrictName", headerName: "District", width: 150 },
        { field: "SiteName", headerName: "Site", width: 150 },
        { field: "ReimbursementTrackingProgram", headerName: "Program", width: 180 },
    ];

    // ───────────────────────────────────────────────────────────
    // UI
    // ───────────────────────────────────────────────────────────
    return (
        <Box m={3} sx={{ bgcolor: theme.palette.background.default, borderRadius: 2, p: 3 }}>
            <Header
                title="FEDERAL REIMBURSEMENT DASHBOARD (Income)"
                subtitle="Track federal reimbursement for income records"
            />

            {/* ── Filters ─────────────────────────────────────────── */}
            <Box display="flex" gap={2} flexWrap="wrap" mb={3}>
                <FormControl sx={{ minWidth: 140 }}>
                    <InputLabel>Month</InputLabel>
                    <Select
                        value={selectedMonth}
                        label="Month"
                        onChange={(e) => setSelectedMonth(e.target.value)}
                    >
                        <MenuItem value="All">All</MenuItem>
                        {monthOptions.map((m) => (
                            <MenuItem key={m} value={m}>{new Date(0, m - 1).toLocaleString("default", { month: "long" })}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl sx={{ minWidth: 180 }}>
                    <InputLabel>Program</InputLabel>
                    <Select
                        value={selectedProgram}
                        label="Program"
                        onChange={(e) => setSelectedProgram(e.target.value)}
                    >
                        <MenuItem value="All">All Programs</MenuItem>
                        {programs.map((p) => (
                            <MenuItem key={p.programid} value={p.programid}>{p.program_abbreviation}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {/* ── Stat boxes ─────────────────────────────────────── */}
            <Box
                display="grid"
                gridTemplateColumns="repeat(auto-fit,minmax(200px,1fr))"
                gap={2}
                mb={4}
            >
                {sortedProgramEntries.map(([prog, total], idx) => (
                    <StatBox
                        key={prog}
                        title={formatCurrency(total)}
                        subtitle={prog}
                        borderColor={BRIGHT_PRIMARY_COLORS[idx % BRIGHT_PRIMARY_COLORS.length]}
                    />
                ))}
            </Box>

            {/* ── Charts (Pie & Line) ─────────────────────────────── */}
            <Box display="flex" flexWrap="wrap" gap={3} mb={4}>
                {/* Pie chart */}
                <Box flex={1} minWidth={300} height={400} sx={{ bgcolor: theme.palette.background.paper, borderRadius: 2, p: 2 }}>
                    <Typography variant="h6" gutterBottom color={theme.palette.text.primary}>
                        Distribution by Program
                    </Typography>
                    <ResponsiveContainer width="100%" height="85%">
                        <PieChart>
                            <Pie data={pieData} dataKey="value" nameKey="name" outerRadius={120} label>
                                {pieData.map((_, i) => (
                                    <Cell key={i} fill={BRIGHT_PRIMARY_COLORS[i % BRIGHT_PRIMARY_COLORS.length]} />
                                ))}
                            </Pie>
                            <RechartsTooltip formatter={formatCurrency} />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                </Box>

                {/* Line chart */}
                <Box flex={2} minWidth={400} height={400} sx={{ bgcolor: theme.palette.background.paper, borderRadius: 2, p: 2 }}>
                    <Typography variant="h6" gutterBottom color={theme.palette.text.primary}>
                        Incomes Over Time (Jul – Jun)
                    </Typography>
                    <ResponsiveContainer width="100%" height="85%">
                        <LineChart data={monthlyData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" stroke={theme.palette.text.primary} />
                            <YAxis stroke={theme.palette.text.primary} />
                            <RechartsTooltip formatter={formatCurrency} />
                            <Legend />
                            {sortedProgramEntries.map(([prog], i) => (
                                <Line
                                    key={prog}
                                    type="monotone"
                                    dataKey={prog}
                                    stroke={BRIGHT_PRIMARY_COLORS[i % BRIGHT_PRIMARY_COLORS.length]}
                                    dot={false}
                                    connectNulls
                                    strokeWidth={3}
                                />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                </Box>
            </Box>

            {/* ── DataGrid ────────────────────────────────────────── */}
            <Box height={500}>
                <StyledDataGrid
                    rows={filtered}
                    columns={columns}
                    getRowId={(row) => row.apiid || row.IncomeID}
                    pageSize={10}
                    rowsPerPageOptions={[10]}
                />
            </Box>
        </Box>
    );
};

export default DashboardReimbursementIncomes;
